import { cn } from '@/lib/utils';
import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, disabled, error, leftIcon, rightIcon, type, ...props }, ref) => {
    if (leftIcon || rightIcon) {
      return (
        <div
          className={cn(
            'flex h-12 w-full items-center gap-2 px-4 font-sans rounded-2xl border bg-surface',
            'border-border transition-[border-color,box-shadow,background-color]',
            'focus-within:border-[hsl(var(--border-focus))] focus-within:ring-1 focus-within:ring-[hsl(var(--border-focus))]',
            'data-[error]:border-destructive data-[error]:ring-1 data-[error]:ring-destructive',
            'data-[disabled]:border-border data-[disabled]:bg-state-disabled-bg/[0.05] data-[disabled]:cursor-not-allowed',
            className
          )}
          data-disabled={disabled || undefined}
          data-error={error || undefined}
        >
          {leftIcon ? (
            <span
              className={cn(
                'flex size-5 shrink-0 items-center justify-center text-muted-foreground-soft',
                error && 'text-destructive'
              )}
              aria-hidden
            >
              {leftIcon}
            </span>
          ) : null}
          <input
            className={cn(
              'h-full w-full min-w-0 flex-1 font-sans text-base text-foreground bg-transparent outline-none',
              'placeholder:text-muted-foreground-soft disabled:text-muted-foreground-soft disabled:[-webkit-text-fill-color:hsl(var(--muted-foreground-soft))] disabled:cursor-not-allowed',
              'file:text-sm file:font-medium file:text-foreground file:border-0 file:bg-transparent',
              'md:text-sm'
            )}
            disabled={disabled}
            ref={ref}
            type={type}
            {...props}
          />
          {rightIcon ? (
            <span
              className="flex size-5 shrink-0 items-center justify-center text-muted-foreground-soft"
            >
              {rightIcon}
            </span>
          ) : null}
        </div>
      );
    }

    return (
      <input
        className={cn(
          'flex h-12 w-full px-4 font-sans text-base text-foreground rounded-2xl border border-border bg-surface',
          'placeholder:text-muted-foreground-soft transition-[border-color,box-shadow,background-color]',
          'focus-visible:border-[hsl(var(--border-focus))] focus-visible:ring-1 focus-visible:ring-[hsl(var(--border-focus))] focus-visible:outline-none',
          'data-[error]:border-destructive data-[error]:ring-1 data-[error]:ring-destructive',
          'disabled:text-muted-foreground-soft disabled:border-border disabled:bg-state-disabled-bg/[0.05] disabled:[-webkit-text-fill-color:hsl(var(--muted-foreground-soft))] disabled:cursor-not-allowed',
          'file:text-sm file:font-medium file:text-foreground file:border-0 file:bg-transparent',
          'md:text-sm',
          className
        )}
        data-error={error || undefined}
        disabled={disabled}
        ref={ref}
        type={type}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input };